import { computed, ref, Ref } from 'vue';
import { Routes } from '@/models/router/router.interface';
import { ComplexModal } from '@studyportals/modal';
import { ModalErrorStates } from '@/models/modal/modal.enum';
import { IDuplicateEntryCampaignDetails } from '@/models/duplicate-entry-campaigns.interface';
import campaignErrorPresenter from '@/presenters/campaign-error-presenter';
import store from '@/store';

export default class ErrorModalCode {
	public modalContent: Ref<HTMLElement | null> = ref(null);
	private modal: Ref<ComplexModal | undefined> = ref(undefined);

	public errorIsRelatedToInvalidCampaignId = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.INVALID_CAMPAIGN_ID;
	});

	public errorIsRelatedToUnsupportedCampaignSetting = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.UNSUPPORTED_CAMPAIGN_SETTING;
	});

	public errorIsRelatedToGetGeneralCampaignData = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.GET_GENERAL_CAMPAIGN_DATA;
	});

	public errorIsRelatedToGetAccountManagers = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.GET_ACCOUNT_MANAGERS;
	});

	public errorIsRelatedToGetClientServicesPeople = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.GET_CLIENT_SERVICES_PEOPLE;
	});

	public errorIsRelatedToSearchOrganisations = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.SEARCH_ORGANISATIONS;
	});

	public errorIsRelatedToGetCustomProducts = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.GET_CUSTOM_PRODUCTS;
	});

	public errorIsRelatedToLoadingGeotargetingWhenStandAlone = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.LOADING_GEOTARGETING_WHEN_STAND_ALONE;
	});

	public errorIsRelatedToGetCampaignReadinessInfo = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.GET_CAMPAIGN_READINESS_INFO;
	});

	public errorIsRelatedToGetCampaignListingEntities = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.GET_CAMPAIGN_LISTING_ENTITIES;
	});

	public errorIsRelatedToACustomProductsOnlyCampaignPageView = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.CUSTOM_PRODUCTS_ONLY_CAMPAIGN_PAGE_VIEW;
	});

	public errorIsRelatedToGetRightsUsers = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.GET_RIGHTS_USERS;
	});

	public errorIsRelatedToSearchRightsUsers = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.SEARCH_RIGHTS_USERS;
	});

	public errorIsRelatedToGetAuditorEntries = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.GET_AUDITOR_ENTRIES;
	});

	public errorIsRelatedToGetPricingProducts = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.GET_PRICING_PRODUCTS;
	});

	public errorIsRelatedToCreateFailedOnDuplicateEntry = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.CREATE_FAILED_ON_DUPLICATE_ENTRY;
	});

	public errorIsRelatedToSaveFailedOnDuplicateEntry = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.SAVE_FAILED_ON_DUPLICATE_ENTRY;
	});

	public errorIsRelatedToCustomProductDateOutOfBounds = computed((): boolean => {
		return this.modalErrorState.value === ModalErrorStates.CUSTOM_PRODUCT_DATE_OUT_OF_BOUNDS;
	});

	public duplicateEntryStartDateShort = computed((): string => {
		if (!this.duplicateEntryCampaignDetails.value) {
			return '';
		}

		return this.duplicateEntryCampaignDetails.value.startDate.split('T')[0];
	});

	public duplicateEntryCampaignDetails = computed((): IDuplicateEntryCampaignDetails | null => {
		return store.getters.duplicateEntryCampaignDetails();
	});

	private modalErrorState = computed((): ModalErrorStates => {
		return store.getters.modalErrorState();
	});

	public invalidCampaignPropertyMessage = computed((): string => {
		return campaignErrorPresenter.invalidCampaignPropertyMessage.value;
	});

	private campaignId = computed((): number => {
		const router = store.getters.router();
		return router.props.id;
	});

	public mounted(): void {
		this.prepareModal();
	}

	public unmounted(): void {
		window.ModalManager.close(this.modal.value);
	}

	public closeModal(): void {
		if (this.duplicateEntryCampaignDetails.value !== null) {
			store.mutations.setDuplicateEntryCampaignDetails(null);
		}

		if (this.errorIsRelatedToACustomProductsOnlyCampaignPageView.value) {
			this.redirectToGeneralPage();
		}
		else if (
			this.errorIsRelatedToUnsupportedCampaignSetting.value &&
			this.invalidCampaignPropertyMessage.value !== '' &&
			this.campaignId.value !== 0
		) {
			this.redirectToTheHomePageOfUniAdmin();
		}

		store.mutations.setModalErrorState(ModalErrorStates.INACTIVE);
	}

	public redirectToTheDuplicateEntry(): void {
		if (!this.duplicateEntryCampaignDetails.value) {
			return;
		}

		window.location.replace(`${webpackDefinitions.UNI_ADMIN_URL}/campaign-editor/#/general/${this.duplicateEntryCampaignDetails.value.id}/`);
	}

	private redirectToTheHomePageOfUniAdmin(): void {
		window.location.href = `${webpackDefinitions.UNI_ADMIN_URL}/organisation/`;
	}

	private prepareModal(): void {
		if (!this.modalContent.value) {
			return;
		}

		this.modal.value = new ComplexModal(this.modalContent.value, {
			cssClassName: 'ErrorModalWrapper',
			destroyOnClose: false,
			onClose: this.closeModal.bind(this)
		});

		window.ModalManager.open(this.modal.value);
	}

	private redirectToGeneralPage(): void {
		store.mutations.setCurrentRoute(Routes.GENERAL);
	}
}
